// L2 — Scrubbing de identificadores en los eventos de Sentry (admin web).
//
// Problema: los breadcrumbs de red por defecto capturan la URL completa de
// cada petición, y muchas URLs de Supabase llevan el UUID del usuario (u otros
// datos personales) en el query string: `/rest/v1/profiles?id=eq.<uuid>`,
// `?email=eq.<x>`, `?phone=eq.<x>`… Ese UUID es un identificador de cuenta →
// vincula el evento de diagnóstico con la identidad del usuario.
//
// Decisión de producto (Opción A): NO desactivar los breadcrumbs — se sigue
// viendo QUÉ petición se hizo (tabla/endpoint) — pero SIN el identificador.
//
// Estrategia de redacción, sin depender de detectar patrones de PII:
//   · El query string se reemplaza ENTERO por `<redacted>`. Como los filtros
//     PostgREST meten los VALORES ahí (id=eq.<uuid>, email=eq.<x>, tokens…),
//     dropear el query entero cubre cualquier dato personal de una vez.
//   · El path se conserva (tabla/endpoint, imprescindible para depurar) pero
//     se le redactan los UUID que puedan ir en segmentos (Storage, rutas de
//     recurso único, claves R2 tipo `{userId}/…`).
//   · En texto libre (mensajes de error, valores de excepción) se redactan los
//     UUID y cualquier query string embebido.

// UUID v1–v5, cualquier caja. Global para reemplazar todas las apariciones.
const UUID_RE = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

// Query string embebido en texto libre: desde `?` hasta el primer espacio o
// delimitador. Se sustituye por `?<redacted>`.
const EMBEDDED_QUERY_RE = /\?[^\s"'<>]*/g;

/** Reemplaza cualquier UUID por `<id>`. */
export function redactUuids(text: string): string {
  return text.replace(UUID_RE, '<id>');
}

/**
 * Redacta una URL preservando esquema+host+path (legible para depurar) pero
 * eliminando el query string entero y los UUID del path.
 *
 * Ej.: `https://x.supabase.co/rest/v1/profiles?id=eq.<uuid>&select=email`
 *   → `https://x.supabase.co/rest/v1/profiles?<redacted>`
 */
export function redactUrl(url: string): string {
  if (typeof url !== 'string' || url.length === 0) return url;
  const qIndex = url.indexOf('?');
  const base = qIndex === -1 ? url : url.slice(0, qIndex);
  const safeBase = redactUuids(base);
  return qIndex === -1 ? safeBase : `${safeBase}?<redacted>`;
}

/** Redacta texto libre: query strings embebidos + UUID. */
export function redactText(text: string): string {
  if (typeof text !== 'string' || text.length === 0) return text;
  return redactUuids(text.replace(EMBEDDED_QUERY_RE, '?<redacted>'));
}

// Formas mínimas (estructurales) de los objetos de Sentry que tocamos. SIN
// index signature a propósito: así los tipos estrictos del SDK (Breadcrumb,
// ErrorEvent de @sentry/react v8) son asignables a estas y el genérico
// preserva el tipo concreto en el retorno (contrato de los hooks).
interface BreadcrumbLike {
  message?: string;
  data?: { url?: unknown };
}
interface EventLike {
  message?: string;
  request?: { url?: unknown; query_string?: unknown };
  exception?: { values?: Array<{ value?: unknown }> };
  breadcrumbs?: BreadcrumbLike[];
}

/**
 * `beforeBreadcrumb` de Sentry.init: limpia la URL de los breadcrumbs de red
 * (fetch/xhr) y cualquier UUID/query en el mensaje. Muta y devuelve el mismo
 * objeto (contrato del hook).
 */
export function scrubBreadcrumb<T extends BreadcrumbLike>(breadcrumb: T): T {
  if (breadcrumb.data && typeof breadcrumb.data.url === 'string') {
    breadcrumb.data.url = redactUrl(breadcrumb.data.url);
  }
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = redactText(breadcrumb.message);
  }
  return breadcrumb;
}

/**
 * `beforeSend` de Sentry.init: segunda red que barre el evento entero por si un
 * identificador se coló fuera de los breadcrumbs (request.url/query_string,
 * mensaje del evento, valores de excepción). Muta y devuelve el evento.
 */
export function scrubEvent<T extends EventLike>(event: T): T {
  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = redactUrl(event.request.url);
    }
    if (event.request.query_string != null) {
      event.request.query_string = '<redacted>';
    }
  }
  if (typeof event.message === 'string') {
    event.message = redactText(event.message);
  }
  const values = event.exception?.values;
  if (Array.isArray(values)) {
    for (const v of values) {
      if (typeof v.value === 'string') v.value = redactText(v.value);
    }
  }
  if (Array.isArray(event.breadcrumbs)) {
    for (const b of event.breadcrumbs) scrubBreadcrumb(b);
  }
  return event;
}
